import { type Dispatch, type SetStateAction, useEffect } from 'react'
import { css } from '@emotion/react'
import {
  Button,
  DesignToken,
  Heading,
  SpacingToken,
  Text,
} from '@livechat/design-system-react-components'

import { type IDeveloperProfile } from '@/apis/apps/interfaces'
import { useTypedDispatch } from '@/store/hooks'
import { sendFeedback } from '@/store/feedback'
import { notificationsCreate } from '@/store/notifications'
import { sendEmail } from '@/utils/browser'
import { NOTIFICATIONS, ToastKind } from '@/constants'
import VotingStars from '../VotingStars'

const headingCss = css`
  margin-top: 0;
  margin-bottom: var(${SpacingToken.Spacing2});
`

const contentCss = css`
  margin-top: var(${SpacingToken.Spacing2});
  margin-bottom: var(${SpacingToken.Spacing4});
  text-wrap: pretty;
`

const justRateButtonCss = css`
  margin-left: 5px;
  color: var(${DesignToken.ActionPrimaryDefault});

  :hover {
    color: var(${DesignToken.ActionPrimaryDefault});
  }
`

interface Props {
  id: string
  rate: number | null
  onClose: () => void
  setRate: Dispatch<SetStateAction<number | null>>
  toggleRatingModal: () => void
  developer: IDeveloperProfile
}

const RateExperienceTooltip = ({
  id,
  onClose,
  rate,
  setRate,
  toggleRatingModal,
  developer,
}: Props) => {
  const dispatch = useTypedDispatch()

  const handleRate = () => {
    if (rate) {
      dispatch(sendFeedback.pending({ id, rate, text: null }))
      onClose()
    } else {
      dispatch(
        notificationsCreate({
          kind: ToastKind.Error,
          content:
            'To rate this app, relevant number of stars must be selected.',
          autoHideDelayTime: NOTIFICATIONS.AUTO_HIDE_DELAY_TIME,
        })
      )
    }
  }

  useEffect(() => {
    return () => setRate(null)
  }, [setRate])

  const developerName = developer?.company || 'vendor'
  const developerEmail = `mailto:${developer?.contactEmail}`

  return (
    <div>
      <Heading size="xs" css={headingCss}>
        Rate your experience
      </Heading>

      <VotingStars rate={rate} setRate={setRate} />

      <Text css={contentCss}>
        We appreciate the time you took to share your feedback by rating this
        app. Don't hesitate to{' '}
        <a
          href={developerEmail}
          onClick={(e) => {
            e.preventDefault()
            sendEmail(developer?.contactEmail)
          }}
        >
          contact {developerName}
        </a>{' '}
        to get help and support.
      </Text>

      <div>
        <Button kind="primary" onClick={toggleRatingModal} size="compact">
          Write a review
        </Button>
        <Button
          kind="plain"
          css={justRateButtonCss}
          onClick={handleRate}
          size="compact"
        >
          Just rate it
        </Button>
      </div>
    </div>
  )
}

export default RateExperienceTooltip
